import { Capacitor, WebView } from '@capacitor/core';
import { Directory, Encoding, Filesystem, FilesystemPlugin } from '@capacitor/filesystem';
import { unzipSync } from 'fflate';
import semver from 'semver';
import { AppVersionData, getAppVersionData } from '../app-version/version-utils';

const SNAPSHOTS_DIR = 'NoCloud/ionic_built_snapshots';
const OTA_STATE_PATH = 'NoCloud/ota-updater-state.json';
const INSTALL_COMPLETE_MARKER = '.ota-install-complete';

// Seconds a staged bundle must survive before it is persisted as the permanent
// boot target. If the app crashes within this window, confirmPending never runs,
// the bundle is never persisted, and the next cold launch reverts to the last
// good bundle automatically.
const CONFIRM_DELAY_MS = 5000;
// After this many launches on an unconfirmed bundle without it becoming healthy,
// give up on it and revert to the built-in bundle.
const MAX_UNCONFIRMED_BOOTS = 3;

export interface OtaConfig {
  appBaseUrl: string;
  enabled: boolean;
  fetchCerts: boolean;
}

export type OtaManifest = {
  version: string;
  /** Build identity (manifest v2). Absent on pre-029 manifests — see getManifestIdentity. */
  buildId?: string;
  bundleUrl: string;
  sha256: string;
  updateConfig: {
    compatibleWithNativeVersion: string; // semver range e.g. ">=7.51.3"
    enforcements: NativeEnforcement;
  };
  certs: string[];
};

type NativeEnforcementEntry = {
  enforcementDate: string;
};

type NativeEnforcement = {
  [nativeVersionRange: string]: NativeEnforcementEntry;
};

export type CheckUpdateResult = {
  enforcementDate: number;
  isUpdateMandatory: boolean;
};

// Persisted crash-safety state. `pending` is a downloaded bundle that has been
// staged (loaded) but not yet confirmed healthy, so not yet persisted as the
// permanent boot target.
type OtaState = {
  // Holds the manifest identity (buildId, or version on pre-v2 manifests), which
  // is also the snapshot directory name.
  pending: { version: string; path: string } | null;
  unconfirmedBoots: number;
};

/**
 * The identity a bundle is cached and detected by. Keyed on `buildId` so a
 * same-version redeploy is still seen as new content (FR-020); a manifest
 * without `buildId` falls back to `version` and behaves as before.
 */
export function getManifestIdentity(manifest: Pick<OtaManifest, 'version' | 'buildId'>): string {
  return manifest.buildId ?? manifest.version;
}

export interface OtaUpdaterDependencies {
  getAppVersionData: () => Promise<AppVersionData>;
  isNativePlatform: () => boolean;
  unzip: (buffer: ArrayBuffer) => Record<string, Uint8Array>;
  checkSemverSatisfies: (version: string, range: string) => boolean;
  // Hex SHA-256 of the downloaded bundle, used to verify integrity before applying.
  sha256Hex: (buffer: ArrayBuffer) => Promise<string>;
  // Schedules the post-boot confirmation. Default waits CONFIRM_DELAY_MS.
  schedule: (fn: () => void) => void;
  webView?: {
    setServerBasePath: (opts: { path: string }) => Promise<void>;
    persistServerBasePath: () => Promise<void>;
  };
  filesystem?: FilesystemPlugin;
  directories?: { Library: Directory; Cache: Directory };
}

export class OtaUpdater {
  private config: OtaConfig;
  private deps: OtaUpdaterDependencies;
  private checkForUpdatePromise: Promise<CheckUpdateResult | null> | null = null;
  private bootReconciled = false;

  constructor(config: OtaConfig, deps: OtaUpdaterDependencies) {
    this.config = config;
    this.deps = deps;
  }

  checkForUpdate(): Promise<CheckUpdateResult | null> {
    // If already running, return the existing promise
    if (this.checkForUpdatePromise) {
      return this.checkForUpdatePromise;
    }

    // Start the update check
    const promise = this.performCheckForUpdate();

    // Store the promise
    this.checkForUpdatePromise = promise;

    // Attach cleanup to clear the stored reference when done
    promise.finally(() => {
      if (this.checkForUpdatePromise === promise) {
        this.checkForUpdatePromise = null;
      }
    });

    // Return the original promise (not wrapped)
    return promise;
  }

  private async performCheckForUpdate(): Promise<CheckUpdateResult | null> {
    if (!this.isOtaEnabled()) return null;

    try {
      // Crash-safety first: reconcile any bundle that was staged on a previous
      // launch (confirm it, discard it if it failed to boot, or revert on a loop).
      await this.reconcileBootHealth();

      const appVersion = await this.deps.getAppVersionData();

      const manifest = await this.fetchManifest();
      if (!manifest?.version) return null;

      const identity = getManifestIdentity(manifest);

      if (this.isWebViewAlreadyOnTarget(appVersion.webViewPath, identity)) return null;

      const isCompatible = this.deps.checkSemverSatisfies(
        appVersion.nativeVersion,
        manifest.updateConfig.compatibleWithNativeVersion,
      );

      if (isCompatible) {
        await this.installOrRestageBundle(manifest);
        return null;
      }

      return this.resolveEnforcementResult(manifest, appVersion.nativeVersion);
    } catch (e) {
      console.error('[OTA: *] Update process failed:', e);
      return null;
    }
  }

  async revertToBundle(): Promise<void> {
    if (!this.deps.webView) {
      console.warn('[OTA] WebView not available, cannot revert');
      return;
    }

    await this.writeOtaState({ pending: null, unconfirmedBoots: 0 });
    await this.deps.webView.setServerBasePath({ path: '' });
    await this.deps.webView.persistServerBasePath();
    window.location.reload();
  }

  async fetchManifest(): Promise<OtaManifest | null> {
    const url = `${this.config.appBaseUrl.replace(/\/$/, '')}/updates/manifest.json`;

    try {
      const res = await fetch(url, { cache: 'no-store' });
      return res.ok ? res.json() : null;
    } catch (e) {
      console.warn('[OTA: *] Failed to fetch manifest:', e);
      return null;
    }
  }

  async fetchBundle(url: string): Promise<ArrayBuffer> {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) throw new Error(`[OTA] Bundle fetch failed with status: ${res.status}`);
    return res.arrayBuffer();
  }

  async fetchCerts(urls: string[]): Promise<string[]> {
    const results = await Promise.allSettled(urls.map((url) => this.fetchCert(url)));

    return results
      .filter((r): r is PromiseFulfilledResult<string | null> => r.status === 'fulfilled')
      .map((r) => r.value)
      .filter((cert): cert is string => cert !== null);
  }

  // Refuse to apply a bundle whose bytes don't match the manifest checksum. This
  // rejects partial/corrupt downloads and any bundle from an untrusted/dev source
  // (e.g. a "local-dev" placeholder), so a broken bundle is never staged.
  private async verifyBundleIntegrity(buffer: ArrayBuffer, expectedSha256: string): Promise<void> {
    const expected = (expectedSha256 ?? '').trim().toLowerCase();
    if (!expected) {
      throw new Error('[OTA] Manifest is missing sha256; refusing to apply.');
    }

    const actual = (await this.deps.sha256Hex(buffer)).trim().toLowerCase();
    if (actual !== expected) {
      throw new Error(
        `[OTA] Bundle checksum mismatch (expected ${expected}, got ${actual}); refusing to apply.`,
      );
    }
  }

  // A valid web bundle must contain a non-empty index.html at its root.
  private assertBundleHasIndex(entries: Record<string, Uint8Array>): void {
    const index = entries['index.html'] ?? entries['./index.html'];
    if (!index || index.length === 0) {
      throw new Error('[OTA] Bundle is missing a non-empty index.html; refusing to apply.');
    }
  }

  async writeEntriesToFilesystem(
    basePath: string,
    entries: Record<string, Uint8Array>,
  ): Promise<void> {
    if (!this.deps.filesystem || !this.deps.directories) {
      throw new Error('[OTA] Filesystem not available');
    }

    const { Library } = this.deps.directories;
    await this.safeMkdir(basePath, Library);

    for (const [name, data] of Object.entries(entries)) {
      if (this.shouldSkipZipEntry(name)) continue;

      const filePath = `${basePath}/${name}`;
      const parentDir = filePath.split('/').slice(0, -1).join('/');

      await this.safeMkdir(parentDir, Library);
      await this.deps.filesystem.writeFile({
        directory: Library,
        path: filePath,
        data: this.toBase64(data),
        recursive: true,
      });
    }
  }

  private isOtaEnabled(): boolean {
    return this.deps.isNativePlatform() && this.config.enabled;
  }

  private async isBundleAlreadyInstalled(identity: string): Promise<boolean> {
    return this.fileExists(`${SNAPSHOTS_DIR}/${identity}/${INSTALL_COMPLETE_MARKER}`);
  }

  private isWebViewAlreadyOnTarget(webViewPath: string, identity: string): boolean {
    return webViewPath.includes(`/${identity}`);
  }

  private async installOrRestageBundle(manifest: OtaManifest): Promise<void> {
    const identity = getManifestIdentity(manifest);

    if (await this.isBundleAlreadyInstalled(identity)) {
      await this.stageBundle(`${SNAPSHOTS_DIR}/${identity}`, identity);
      return;
    }

    await this.downloadAndApplyBundle(manifest);
  }

  private async downloadAndApplyBundle(manifest: OtaManifest): Promise<void> {
    if (!this.deps.filesystem || !this.deps.directories) {
      throw new Error('[OTA] Filesystem not available');
    }

    const { Library, Cache } = this.deps.directories;
    const identity = getManifestIdentity(manifest);
    const bundlePath = `${SNAPSHOTS_DIR}/${identity}`;

    const buffer = await this.fetchBundle(manifest.bundleUrl);

    // Verify BEFORE writing/staging so a corrupt bundle never reaches the WebView.
    await this.verifyBundleIntegrity(buffer, manifest.sha256);
    const entries = this.deps.unzip(buffer);
    this.assertBundleHasIndex(entries);

    await this.safeMkdir(SNAPSHOTS_DIR, Library);
    await this.cleanupDir(bundlePath, Library);
    await this.writeEntriesToFilesystem(bundlePath, entries);

    if (this.config.fetchCerts) {
      const certs = await this.fetchCerts(manifest.certs);
      await this.writeCerts(bundlePath, certs, Cache);
    }

    // Reverting always goes to the built-in bundle, so previous snapshots are not
    // needed for rollback — clean them up before we swap (the swap reloads).
    await this.cleanupOldSnapshots(identity, Library);

    await this.stageBundle(bundlePath, identity);
  }

  private async writeCerts(bundlePath: string, certs: string[], cacheDir: any): Promise<void> {
    if (!this.deps.filesystem) {
      throw new Error('[OTA] Filesystem not available');
    }

    await this.deps.filesystem.writeFile({
      directory: cacheDir,
      path: `${bundlePath}/certs.json`,
      data: JSON.stringify(certs),
      encoding: Encoding.UTF8,
      recursive: true,
    });
  }

  private resolveEnforcementResult(
    manifest: OtaManifest,
    nativeVersion: string,
  ): CheckUpdateResult | null {
    if (!manifest.updateConfig.enforcements) return null;

    const enforcementDate = this.getEnforcementForNativeVersion(
      manifest.updateConfig.enforcements,
      nativeVersion,
    );

    if (enforcementDate === null) return null;

    return {
      enforcementDate,
      isUpdateMandatory: enforcementDate <= Date.now(),
    };
  }

  private getEnforcementForNativeVersion(
    enforcement: Record<string, { enforcementDate: string }>,
    nativeVersion: string,
  ): number | null {
    let soonest: number | null = null;

    for (const [range, { enforcementDate }] of Object.entries(enforcement)) {
      if (!this.deps.checkSemverSatisfies(nativeVersion, range)) continue;

      const ms = new Date(enforcementDate).getTime();
      if (soonest === null || ms < soonest) {
        soonest = ms;
      }
    }

    return soonest;
  }

  private async fetchCert(url: string): Promise<string | null> {
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) return null;

      const text = await res.text();
      return text.trim() || null;
    } catch (e) {
      console.warn(`[OTA] Failed to fetch cert from ${url}:`, e);
      return null;
    }
  }

  // Load a downloaded bundle for THIS session without persisting it. A crash before
  // confirmPending() leaves the persisted (last good) path untouched, so the next
  // cold launch reverts automatically. persistServerBasePath() runs only in
  // confirmPending(), after the bundle has proven it can boot.
  private async stageBundle(pathInLibrary: string, identity: string): Promise<void> {
    if (!this.deps.webView || !this.deps.filesystem || !this.deps.directories) {
      throw new Error('[OTA] WebView or Filesystem not available');
    }

    const { Library } = this.deps.directories;
    const { uri } = await this.deps.filesystem.getUri({
      directory: Library,
      path: pathInLibrary,
    });

    const path = uri.startsWith('file://') ? uri.substring(7) : uri;

    // Record the pending bundle BEFORE swapping so a crash mid-swap is recoverable.
    await this.persistOtaState({
      pending: { version: identity, path: pathInLibrary },
      unconfirmedBoots: 0,
    });
    await this.markInstallComplete(pathInLibrary, identity);

    await this.deps.webView.setServerBasePath({ path });
    // Deliberately NOT persistServerBasePath() here.
  }

  // Run once per launch. Reconcile any bundle staged on a previous launch:
  //  - not running it now  -> it failed to boot; discard it (cold launch reverted).
  //  - too many attempts   -> give up; revert to the built-in bundle.
  //  - otherwise           -> count this attempt and confirm after a stability delay.
  private async reconcileBootHealth(): Promise<void> {
    if (this.bootReconciled) return;
    this.bootReconciled = true;

    const state = await this.readOtaState();
    if (!state.pending) return;

    let webViewPath = '';
    try {
      webViewPath = (await this.deps.getAppVersionData()).webViewPath;
    } catch {
      // best-effort; treat as not-on-pending below
    }

    const onPendingBundle = webViewPath.includes(`/${state.pending.version}`);

    if (!onPendingBundle) {
      console.warn(
        '[OTA] Staged bundle did not boot; discarding and staying on the last good bundle:',
        state.pending.version,
      );
      await this.discardPending(state.pending.path);
      return;
    }

    if (state.unconfirmedBoots >= MAX_UNCONFIRMED_BOOTS) {
      console.error('[OTA] Staged bundle failed to confirm repeatedly; reverting to built-in.');
      await this.discardPending(state.pending.path);
      await this.revertToBundle();
      return;
    }

    await this.writeOtaState({
      pending: state.pending,
      unconfirmedBoots: state.unconfirmedBoots + 1,
    });

    this.deps.schedule(() => {
      void this.confirmPending();
    });
  }

  // Called after a staged bundle has survived the stability delay: lock it in.
  private async confirmPending(): Promise<void> {
    const state = await this.readOtaState();
    if (!state.pending) return;

    try {
      await this.deps.webView?.persistServerBasePath();
      await this.writeOtaState({ pending: null, unconfirmedBoots: 0 });
    } catch (e) {
      console.warn('[OTA] Failed to persist confirmed bundle; will retry next boot', e);
    }
  }

  private async markInstallComplete(pathInLibrary: string, identity: string): Promise<void> {
    if (!this.deps.filesystem || !this.deps.directories) {
      throw new Error('[OTA] Filesystem not available');
    }

    await this.deps.filesystem.writeFile({
      directory: this.deps.directories.Library,
      path: `${pathInLibrary}/${INSTALL_COMPLETE_MARKER}`,
      data: identity,
      encoding: Encoding.UTF8,
      recursive: true,
    });
  }

  private async discardPending(pathInLibrary: string): Promise<void> {
    if (this.deps.directories) {
      await this.cleanupDir(pathInLibrary, this.deps.directories.Library);
    }
    await this.writeOtaState({ pending: null, unconfirmedBoots: 0 });
  }

  private async readOtaState(): Promise<OtaState> {
    const empty: OtaState = { pending: null, unconfirmedBoots: 0 };
    if (!this.deps.filesystem || !this.deps.directories) return empty;

    try {
      const { data } = await this.deps.filesystem.readFile({
        directory: this.deps.directories.Library,
        path: OTA_STATE_PATH,
        encoding: Encoding.UTF8,
      });
      const parsed = JSON.parse(
        typeof data === 'string' ? data : String(data),
      ) as Partial<OtaState>;
      return {
        pending: parsed.pending ?? null,
        unconfirmedBoots: typeof parsed.unconfirmedBoots === 'number' ? parsed.unconfirmedBoots : 0,
      };
    } catch {
      return empty;
    }
  }

  private async writeOtaState(state: OtaState): Promise<void> {
    try {
      await this.persistOtaState(state);
    } catch (e) {
      console.warn('[OTA] Failed to write OTA state', e);
    }
  }

  private async persistOtaState(state: OtaState): Promise<void> {
    if (!this.deps.filesystem || !this.deps.directories) return;

    await this.deps.filesystem.writeFile({
      directory: this.deps.directories.Library,
      path: OTA_STATE_PATH,
      data: JSON.stringify(state),
      encoding: Encoding.UTF8,
      recursive: true,
    });
  }

  private shouldSkipZipEntry(name: string): boolean {
    return name.endsWith('/') || name.startsWith('__MACOSX') || name.includes('..');
  }

  private toBase64(buffer: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < buffer.byteLength; i++) {
      binary += String.fromCharCode(buffer[i]);
    }
    return btoa(binary);
  }

  private async fileExists(path: string): Promise<boolean> {
    if (!this.deps.filesystem || !this.deps.directories) {
      return false;
    }

    try {
      const stat = await this.deps.filesystem.stat({
        directory: this.deps.directories.Library,
        path,
      });
      return stat.type === 'file';
    } catch {
      return false;
    }
  }

  private async safeMkdir(path: string, directory: any): Promise<void> {
    if (!this.deps.filesystem) {
      throw new Error('[OTA] Filesystem not available');
    }

    try {
      await this.deps.filesystem.mkdir({ directory, path, recursive: true });
    } catch {
      // Directory likely already exists; recursive flag handles most cases
    }
  }

  private async cleanupDir(path: string, directory: any): Promise<void> {
    if (!this.deps.filesystem) {
      throw new Error('[OTA] Filesystem not available');
    }

    try {
      await this.deps.filesystem.rmdir({ directory, path, recursive: true });
    } catch {
      // Directory may not exist yet
    }
  }

  private async cleanupOldSnapshots(retainIdentity: string, directory: any): Promise<void> {
    if (!this.deps.filesystem) {
      throw new Error('[OTA] Filesystem not available');
    }

    try {
      const { files } = await this.deps.filesystem.readdir({
        directory,
        path: SNAPSHOTS_DIR,
      });

      const deletions = files
        .filter((f) => f.name !== retainIdentity)
        .map((f) => this.cleanupDir(`${SNAPSHOTS_DIR}/${f.name}`, directory));

      await Promise.allSettled(deletions);
    } catch {
      // Snapshots directory may not exist yet
    }
  }
}

/**
 * Creates an OTA updater using default Capacitor dependencies.
 * Returns a cached singleton instance to prevent concurrent operations.
 */
let cachedOtaUpdater: OtaUpdater | null = null;

export function createCapacitorOtaUpdater(config: OtaConfig): OtaUpdater {
  if (cachedOtaUpdater) {
    return cachedOtaUpdater;
  }

  cachedOtaUpdater = new OtaUpdater(config, {
    getAppVersionData,
    isNativePlatform: () => Capacitor.isNativePlatform(),
    unzip: (buffer) => unzipSync(new Uint8Array(buffer)),
    checkSemverSatisfies: (version, range) => semver.satisfies(version, range),
    sha256Hex: async (buffer) => {
      const digest = await crypto.subtle.digest('SHA-256', buffer);
      return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    },
    schedule: (fn) => {
      setTimeout(fn, CONFIRM_DELAY_MS);
    },
    webView: WebView,
    filesystem: Filesystem,
    directories: { Library: Directory.Library, Cache: Directory.Cache },
  });

  return cachedOtaUpdater;
}
